/**
 * Web component for the country popup
 * Uses 2 types of cookies
 *
 */

(async () => {
  if (!customElements.get('country-popup')) {
    const { Fancybox } = await import('@fancyapps/ui');
    const { default: Cookies } = await import('js-cookie');

    class CountryPopup extends HTMLElement {
      constructor() {
        super();

        // Default options
        this.options = {
          apiUrl: 'https://extreme-ip-lookup.com/json',
          apiFields: {
            country: 'country_code2',
            language: 'languages',
            currency: 'currency',
          },
          storeLocalization: {
            country: null,
            language: null,
            currency: null,
          },
          delay: 0,
          expiresInDays: 0,
          currentCountryCode: '',
          countryData: {},
        };

        // Get options from element data and merge it with this.options
        if (this?.dataset?.options) {
          const dataOptions = JSON.parse(this.dataset.options);
          this.options = {
            ...this.options,
            ...dataOptions,
          };
        }

        // Default state
        this.state = {
          country: document.querySelector(`[name='language_code']`),
          language: document.querySelector(`[name='country_code']`),
        };

        // If options.expiresInDays is set to zero, remove cookie
        if (this.options.expiresInDays == 0) {
          this.removeCookie();
        }

        // Selectors
        this.popup = this.querySelector('[data-country-popup-wrapper]');
        this.openButtons = document.querySelectorAll('[data-open-country-popup]');
        this.confirmButton = this.querySelector('button[type="submit"]');

        // Init country selector dropdowns
        this.initSelectors();

        // Init country popup button
        this.initPopupButton();

        // Get user/browser localization
        this.getUserLocalization().then(() => {
          // Show popup based on localization
          this.showBasedOnLocal();
        });

        //On click confirm button, remove cookie for 'country_popup_open'
        this.confirmButton.addEventListener('click', () => {
          Cookies.remove('country_popup_open');
        });

        // Open country selector event
        this.openButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.open('default');
            // reset country selector
            document
              .querySelector(
                `[data-selector="country"] [data-iso-code="${this.options.storeLocalization.country}"]`
              )
              ?.click();
            document
              .querySelector(
                `[data-selector="language"] [data-iso-code="${this.options.storeLocalization.language_iso_code}"]`
              )
              ?.click();
          });
        });
      }

      /**
       * Set cookie expired in days country popup cookie
       * @param {object} cookie data
       * @returns {Void}
       */
      setCookie(data) {
        Cookies.set(`country_popup`, JSON.stringify(data), {
          expires: parseInt(this.options.expiresInDays),
        });
      }

      /**
       * Get expired in days country popup cookie
       * @returns {String}
       */
      getCookie() {
        if (!Cookies.get(`country_popup`)) {
          return;
        }
        return JSON.parse(Cookies.get(`country_popup`));
      }

      /**
       * Remove expired in days country popup cookie
       * @returns {Void}
       */
      removeCookie() {
        Cookies.remove(`country_popup`);
        Cookies.remove('country_popup_open');
      }

      /**
       * Get localization data from cookies or geolocation API
       * @returns {object} with the users localization
       */
      async getUserLocalization() {
        // Else fetch from geo api
        try {
          // Try to get the country from the cookies
          this.userLocalization = this.getCookie() ? this.getCookie() : {};
          if (this.userLocalization.country) {
            return;
          }

          // Else fetch from api
          let response = await fetch(this.options.apiUrl);
          let data = await response.json();

          // Get Api field names from options
          const { country, language, currency } = this.options.apiFields;

          // Check if country is found
          if (country in data) {
            this.userLocalization.country = data[country];
          } else {
            throw new Error(`${country} is not found in response object.`);
          }

          // Check if language is found
          if (language in data) {
            this.userLocalization.language = data[language].split(',');
          } else {
            throw new Error(`${language} is not found in response object.`);
          }

          // Check if currency is found
          if (currency in data) {
            this.userLocalization.currency = data[currency];
          } else {
            throw new Error(`${currency} is not found in response object.`);
          }

          // Set state to show the popup
          this.userLocalization.show = true;
          this.userLocalization.showBasedOnLocal = true;

          this.setCookie(this.userLocalization);
        } catch (error) {
          this.userLocalization.showBasedOnLocal = false;
          console.error(error);
        }
      }

      /**
       * Show country popup based on localization
       * Get country location from cookie or GeoAPI
       * @returns {String} The country code, for example 'NL'
       */
      showBasedOnLocal() {
        // Check if popup should be shown based on previous choices
        if (!this.userLocalization.showBasedOnLocal) {
          return;
        }

        // Check if popup is already shown and closed
        if (!this.userLocalization.show) {
          return;
        }

        // Check if store localization is user localization
        if (
          this.userLocalization.country == this.options.storeLocalization.country &&
          (this.userLocalization.language.includes(this.options.storeLocalization.language) ||
            this.userLocalization.language.includes(this.options.storeLocalization.country)) &&
          this.userLocalization.currency.code == this.options.storeLocalization.currency
        ) {
          return;
        }

        // Get Recommendation country / currency
        let country = this?.options.countryData.filter((data) => {
          if (
            data.type == 'country' &&
            (data.iso_code == this.userLocalization.country ||
              data.alt_iso_codes.includes(this.userLocalization.country))
          ) {
            return true;
          }
          return false;
        })[0];

        // Get Recommendation language
        let language = this?.options.countryData.filter((data) => {
          if (
            data.type == 'language' &&
            this.userLocalization.language.join(',').includes(data.iso_code)
          ) {
            return true;
          }
          return false;
        })[0];

        // Return if no language and country found
        if (!language && !country) {
          return;
        }

        // Select the right language and country by default
        this.querySelector(
          `[data-selector="country"] [data-iso-code="${country.iso_code}"]`
        )?.click();
        this.querySelector(
          `[data-selector="language"] [data-iso-code="${language.iso_code}"]`
        )?.click();

        // Open popup
        setTimeout(() => {
          this.open('recommendation');
        }, this.options.delay);
      }

      /**
       * Open the country popup
       * @type = 'default' or 'recommendation'
       * @returns void
       */
      open(type) {
        //Set cookie for that the popup os open
        Cookies.set('country_popup_open');
        // Close button event listener
        this.querySelectorAll('[data-country-popup-close]').forEach((button) => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this.close();
          });
        });

        // Open fancybox and set callback for destroy event
        Fancybox.show(
          [
            {
              src: this.popup,
              type: 'html',
            },
          ],
          {
            closeButton: false,
            dragToClose: false,
            width: 280,
            mainClass: 'fancybox-country-popup',
            trapFocus: false, // xFIX klaviyo is open above fancybox
          }
        );

        // Select the right intro and stay button
        if (type == 'recommendation') {
          document.querySelector('[data-country-popup-intro="default"]').classList.add('hidden');
          document
            .querySelector('[data-country-popup-intro="recommendation"]')
            .classList.remove('hidden');
          document.querySelector('[data-country-popup-stay]').classList.remove('hidden');
        } else {
          document.querySelector('[data-country-popup-intro="default"]').classList.remove('hidden');
          document
            .querySelector('[data-country-popup-intro="recommendation"]')
            .classList.add('hidden');
          document.querySelector('[data-country-popup-stay]').classList.add('hidden');
        }
      }

      /**
       * Close country popup
       * @returns void
       */
      close() {
        Cookies.remove('country_popup_open');
        this.userLocalization.show = false;
        this.setCookie(this.userLocalization);
        Fancybox.close();
      }

      /**
       * init language and currency selector
       * @returns {Void}
       */
      initSelectors() {
        this.querySelectorAll('[data-selector]').forEach((selector) => {
          const type = selector.dataset.selector;
          selector.querySelectorAll('[data-iso-code]').forEach((button) => {
            button.addEventListener('click', (e) => {
              e.preventDefault();

              let isoCode = button.dataset.isoCode.trim();
              let value = button.innerHTML.trim();

              // Check for country also alt types
              if (type == 'country' && button.dataset.altIsoCodes) {
                if (button.dataset.altIsoCodes.includes(this.state.language)) {
                  isoCode = this.state.language;
                }
              }

              // Update selected value in selector
              selector.querySelector('[data-selector-value]').innerHTML = value;

              // Set active class for option
              selector.querySelectorAll('[data-iso-code]').forEach((item) => {
                item.classList.remove('active');
              });
              button.classList.add('active');

              // Update ISO code in localization input field
              document.querySelectorAll(`[name='${type}_code']`).forEach((input) => {
                input.value = isoCode;
              });

              // Close selector
              selector.querySelector('[data-options]').remove();

              // Update the state
              this.state[type] = isoCode;

              // Check if country also should be update on language click
              if (type == 'language') {
                document
                  ?.querySelector('[data-selector="country"]')
                  ?.querySelector('[data-iso-code].active')
                  ?.click();
              }
            });
          });
        });
      }

      /**
       * Init country popup button
       * @returns {Void}
       */
      initPopupButton() {
        // Get Recommendation country / currency
        let country = this?.options.countryData.filter((data) => {
          if (
            data.type == 'country' &&
            (data.iso_code == this.options.storeLocalization.country ||
              data.alt_iso_codes.includes(this.options.storeLocalization.country))
          ) {
            return true;
          }
          return false;
        })[0];

        // Get Recommendation language
        let language = this?.options.countryData.filter((data) => {
          if (
            data.type == 'language' &&
            this.options.storeLocalization.language_iso_code.includes(data.iso_code)
          ) {
            return true;
          }
          return false;
        })[0];

        // Return if no language and country found
        if (!language || !country) {
          return;
        }

        const image = language.image
          ? `<img src="${language.image}" alt="${language.iso_code}" class="rounded-full w-4 h-4 inline-flex" />`
          : '';

        const html = `
          ${image} ${language.title} | ${country.title}
        `;

        document.querySelectorAll('[data-open-country-popup]').forEach((button) => {
          button.innerHTML = html;
        });
      }
    }

    window.CountryPopup = CountryPopup;
    customElements.define('country-popup', CountryPopup);
  }
})();
